import { Auth0Client, createAuth0Client } from '@auth0/auth0-spa-js';
import { AuthenticationHandler, AuthenticationResult, AuthenticationConfig } from './types';
import { appInsights } from '../../../utils/appInsights';
import { handleNativeAppLogin } from '../../../utils/wrapperAppFunctions';

class LegacyAppAuthHandler implements AuthenticationHandler {
  private auth0Client?: Auth0Client;

  private config: AuthenticationConfig;

  constructor(config: AuthenticationConfig) {
    this.config = config;
  }

  async initialize(): Promise<AuthenticationResult> {
    try {
      appInsights.trackEvent({
        name: '[Auth] Initializing Legacy App Auth0Client',
        properties: { mode: 'legacy-app' }
      });

      this.auth0Client = await createAuth0Client({
        domain: this.config.domain,
        clientId: this.config.clientId,
        authorizationParams: {
          redirect_uri: 'brobizz://callback',
          audience: this.config.audience
        },
        cacheLocation: 'localstorage',
        useRefreshTokens: true
      });

      const query = window.location.search;
      const shouldHandleRedirect = query.includes('code=') && query.includes('state=');

      if (shouldHandleRedirect) {
        await this.auth0Client.handleRedirectCallback();
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      try {
        await this.auth0Client.getTokenSilently();

        const searchParams = new URLSearchParams(document.location.search);
        if (searchParams.get('error_description') === 'User cancelled') {
          window.history.replaceState({}, document.title, window.location.pathname);
        }

        return {
          isAuthenticated: true,
          auth0Client: this.auth0Client
        };
      } catch (error) {
        const searchParams = new URLSearchParams(window.location.search);
        const isAccessDenied = searchParams.get('error') === 'access_denied';

        if (!isAccessDenied) {
          await handleNativeAppLogin(this.auth0Client);
          appInsights.trackEvent({
            name: 'NativeAppLogin',
            properties: { mode: 'legacy-app' }
          });

          return {
            isAuthenticated: false,
            shouldShowRedirectMessage: true,
            auth0Client: this.auth0Client
          };
        }

        if (searchParams.get('error_description') === 'User cancelled') {
          this.auth0Client.loginWithRedirect();
        } else {
          appInsights.trackEvent({
            name: 'FailedLogin',
            properties: { reason: 'access_denied', mode: 'legacy-app' }
          });
        }

        return {
          isAuthenticated: false,
          isAccessDenied,
          auth0Client: this.auth0Client
        };
      }
    } catch (error) {
      appInsights.trackException({
        exception: error instanceof Error ? error : new Error(String(error))
      });

      return {
        isAuthenticated: false,
        auth0Client: this.auth0Client
      };
    }
  }

  async refresh(): Promise<string | null> {
    if (!this.auth0Client) {
      return null;
    }

    try {
      return await this.auth0Client.getTokenSilently();
    } catch (error) {
      appInsights.trackException({
        exception: error instanceof Error ? error : new Error(String(error))
      });
      return null;
    }
  }
}

export default LegacyAppAuthHandler;
